import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SplitButton } from "@progress/kendo-react-buttons";
import { setShowTeamSidebar } from "../../../../actions/teamSidebar";
import { useActionRegistry } from "../../../../components/App/ActionRegistryContext";
import { AutomationProvider } from "../../../../components/Automation/Automation.context";
import AddAutomationPopup from "../../../../components/Automation/AutomationPage/AddAutomationPopup";
import CreateProjectPopup from "../../../../components/Projects/CreateProject/CreateProjectPopup";
import { ReminderContextProvider } from "../../../../components/Tasks/Reminders/ReminderContextProvider";
import { canUserManageTeamChipsProject } from "../../../../components/TeamSetting/TeamChips/projectVariables.helper";
import { label, notifyIcon, quote } from "../../../../config";
import { checkAdmin } from "../../../../utils/common";
import { getNotification } from "../../../../utils/common";
import AddMembersPopup from "./AddMembersPopup";
import AddReminderPopup from "./AddReminderPopup";
import ChipTypeMainPopup from "./ChipTypeMainPopup";
import { useHeaderAnchor } from "./HeaderAnchorContext";
import JoinTeamPopup from "./JoinTeamPopup";
import { SplitButtonConfigs } from "./SplitButtonConfig";

/**
 * Parent Comp : Header
 * This renders the split button in the header based on config
 * @author Sarthak Arora
 */
const HeaderSplitButton = ({ showReminderPopup, setShowReminderPopup, addButtonRef, projectId }) => {
  const { getAction } = useActionRegistry();
  const headerAnchorRef = useHeaderAnchor();
  const buttonType = useSelector((state) => state.auth.user.splitButtonType);
  const { user } = useSelector((state) => state.auth);
  const { projectDetails } = useSelector((state) => state.projectSidebar || {});
  const { visitingByAdmin } = useSelector((state) => state.userDetails);
  const buttonConfig = SplitButtonConfigs[buttonType];
  const isAddChipSplit = buttonConfig?.itemId === "addChip";
  const canManageTeamChips = useMemo(() => {
    if (!isAddChipSplit) return true;
    if (visitingByAdmin) return false;
    return canUserManageTeamChipsProject({
      isAdmin: user?.isAdmin,
      roleId: projectDetails?.RoleId,
      projectTeamId: projectDetails?.ProjectId ?? projectId,
      userMyProjectId: user?.myProjectId,
    });
  }, [isAddChipSplit, visitingByAdmin, user?.isAdmin, user?.myProjectId, projectDetails?.RoleId, projectDetails?.ProjectId, projectId]);

  const addProjectRef = useRef(null);
  const [show, setShow] = useState(false);
  const [showAutomationPopup, setShowAutomationPopup] = useState(false);
  const [showAddMembersPopup, setShowAddMembersPopup] = useState(false);
  const [showJoinTeamPopup, setShowJoinTeamPopup] = useState(false);
  const [showChipTypePopup, setShowChipTypePopup] = useState(false);
  const dispatch = useDispatch();
  const splitButtonContainerRef = useRef(null);
  const displayProjectName = user?.operationalTeamName || label.TEAM;
  const displayTeamChipName = (user?.operationalTeamName || "Project").replace(/s$/i, "") || "Project";

  // Sync the local ref with the context ref so other components can use it
  const setRef = (node) => {
    addProjectRef.current = node;
    if (node && headerAnchorRef) {
      headerAnchorRef.current = node;
    }
  };

  const closeChipTypePopup = useCallback(() => {
    setShowChipTypePopup(false);
  }, []);

  useEffect(() => {
    setShowChipTypePopup(false);
  }, [buttonType]);

  /**Check if the button should be disabled for non-admin users*/
  const isAddWorkspaceButton = buttonConfig?.itemId === "addWorkspace";
  const isUserAdmin = checkAdmin(user.isAdmin);
  const shouldDisableAddChipMain = isAddChipSplit && !canManageTeamChips;
  const shouldDisableMainAction = (isAddWorkspaceButton && !isUserAdmin) || shouldDisableAddChipMain;

  /**
   * handler for the item in the split dropdown
   * @param {*} e
   */
  const handleItemClick = (e) => {
    if (!buttonConfig) return;
    closeChipTypePopup();
    const itemId = buttonConfig.items[e.itemIndex]?.id;

    if (itemId === "addTeam") {
      setShow(!show);
      return;
    }

    if (itemId === "manageProjectsJoinProject") {
      setShowJoinTeamPopup(true);
      return;
    }
    const handler = getAction(itemId);

    if (handler) {
      if (itemId === label.ADD_REMINDER_ACTION && setShowReminderPopup) {
        setShowReminderPopup(!showReminderPopup);
      } else {
        handler();
      }
    }
  };

  /**
   * handler for the main split button
   * @param {*} e
   */
  const handleButtonClick = (e) => {
    if (!buttonConfig) return;

    if (shouldDisableAddChipMain) {
      getNotification(quote.TEAM_CHIPS_MANAGE_DISABLED, notifyIcon.WARNING_ICON);
      return;
    }

    if (isAddWorkspaceButton && !isUserAdmin) {
      return;
    }

    if (buttonConfig.itemId === "manageProjectsAddProject") {
      setShow(!show);
      return;
    }
    if (buttonConfig.itemId === "addTeam") {
      setShowJoinTeamPopup(true);
      return;
    }
    if (buttonConfig.itemId === "addAutomation") {
      if (visitingByAdmin) {
        getNotification(quote.AUTOMATION_ADD_DISABLED, notifyIcon.WARNING_ICON);
        return;
      }

      setShowAutomationPopup(!showAutomationPopup);
      return;
    }
    if (buttonConfig.itemId === "addWorkspace") {
      dispatch(setShowTeamSidebar({ showQueueSidebar: true }));
      return;
    }

    if (buttonConfig.itemId === "addMember") {
      setShowAddMembersPopup(!showAddMembersPopup);
      return;
    }

    if (buttonConfig.itemId === "addChip") {
      setShowChipTypePopup((open) => !open);
      return;
    }

    const itemId = buttonConfig.itemId;
    const handler = getAction(itemId);

    if (handler) {
      if (itemId === label.ADD_REMINDER_ACTION && setShowReminderPopup) {
        setShowReminderPopup(!showReminderPopup);
      } else {
        handler();
      }
    }
  };

  const popupSettings = {
    popupClass: "custom-split-popup",
    anchorAlign: { vertical: "bottom", horizontal: "right" },
    popupAlign: { vertical: "top", horizontal: "right" },
  };
  if (!buttonConfig) return null;

  const buttonText = typeof buttonConfig.text === "function" ? buttonConfig.text(displayProjectName) : buttonConfig.text;

  const buttonContent = (
    <div title={buttonConfig.tooltip} className='d-flex align-items-end' ref={setRef}>
      <span className='mr-2 add-task-icon'>{buttonConfig.icon}</span>
      <span>{buttonText}</span>
    </div>
  );

  /**  Add disabled class for visual appearance while keeping dropdown functional*/
  const buttonClassName = `btn-width btn btn-primary mr-4 tour-tasks-add-task ${shouldDisableMainAction ? "disabled-button" : ""}`;

  return (
    <>
      <div ref={splitButtonContainerRef}>
        <SplitButton
          popupSettings={popupSettings}
          className={buttonClassName}
          text={buttonConfig.itemId === "addTask" ? <span title={buttonConfig.title}>{buttonContent}</span> : buttonContent}
          items={buttonConfig.items.map((item) => {
            const chipDisplayName = buttonConfig.itemId === "addChip" ? displayTeamChipName : displayProjectName;
            const itemText = typeof item.text === "function" ? item.text(chipDisplayName) : item.text;
            const itemContent = (
              <>
                {item.icon ? <span className='mr-2'>{item.icon}</span> : null}
                {itemText}
              </>
            );
            return {
              text: (
                <span  id={item?.testId ? item?.testId : ""} className='d-flex align-items-center split-btn-menu-item'>
                  {item.icon ? <span className='mr-2 split-btn-menu-item-icon'>{item.icon}</span> : null}
                  <span className='split-btn-menu-item-label'>{itemText}</span>
                </span>
              ),
            };
          })}
          onItemClick={handleItemClick}
          onButtonClick={handleButtonClick}
          onBlur={()=>{}}
        />
        {buttonConfig.itemId === "addChip" && buttonConfig.mainMenuItems?.length ? (
          <ChipTypeMainPopup
            show={showChipTypePopup}
            onClose={closeChipTypePopup}
            anchorRef={addProjectRef}
            splitButtonRootRef={splitButtonContainerRef}
            menuItems={buttonConfig.mainMenuItems}
            displayTeamChipName={displayTeamChipName}
          />
        ) : null}
      </div>
      {show && <CreateProjectPopup fromHeader={true} anchor={addProjectRef} show={show} setShow={setShow} />}
      {showAutomationPopup && (
        <AutomationProvider projectId={projectId} shouldFetchAutomations={false}>
          <AddAutomationPopup anchor={addProjectRef} show={showAutomationPopup} onClose={() => setShowAutomationPopup(false)} projectId={projectId} />
        </AutomationProvider>
      )}
      {showAddMembersPopup && (
        <AddMembersPopup anchor={addProjectRef} show={showAddMembersPopup} onClose={() => setShowAddMembersPopup(false)} projectId={projectId} />
      )}
      {showReminderPopup && (
        <ReminderContextProvider>
          <AddReminderPopup
            fromHeader={true}
            showReminderPopup={showReminderPopup}
            setShowReminderPopup={setShowReminderPopup}
            popupAnchor={addButtonRef}
          />
        </ReminderContextProvider>
      )}
      {showJoinTeamPopup && (
        <JoinTeamPopup fromHeader={true} anchor={addProjectRef} show={showJoinTeamPopup} onClose={() => setShowJoinTeamPopup(false)} />
      )}
    </>
  );
};

export default HeaderSplitButton;
